import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef } from 'react';

import { useActiveThreadStoreApi } from '../ActiveThreadStoreProvider';
import {
    useCommentsGroupStore,
    useCommentsGroupStoreApi,
    useOptionalCommentsGroupStoreApi,
} from './comments-group-context';
import type { CommentsGroupActivation } from './comments-group-store';
import type { GroupCommentEntry } from './merge-group-comments';

export type GroupThreadActivation = {
    readonly activation: CommentsGroupActivation | null;
    readonly activateEntry: (entry: GroupCommentEntry) => void;
    readonly clearActivation: () => void;
};

export const useGroupThreadActivation = (): GroupThreadActivation => {
    const store = useCommentsGroupStoreApi();
    const activation = useCommentsGroupStore((state) => state.activation);

    const activateEntry = useCallback(
        ({ comment, source }: GroupCommentEntry) =>
            store.getState().activateThread({
                memberId: source.memberId,
                target: comment.target,
                threadId: comment.id,
            }),
        [store],
    );

    const clearActivation = useCallback(() => store.getState().clearActivation(), [store]);

    return { activation, activateEntry, clearActivation };
};

/**
 * Keeps one surface's own active-thread store and the group activation in step, both ways:
 * group activation drives the local store so the surface highlights the activated thread, and
 * a local change away from the mirrored thread — outside click, composer cancel, thread delete,
 * scope reset — releases the group activation so no other surface stays lit.
 *
 * `memberId` null means "shows every member's threads" (the group comment tile); a member id
 * means the surface only owns activations pointing at that member.
 */
const useGroupActivationMirror = (memberId: string | null): void => {
    const store = useOptionalCommentsGroupStoreApi();
    const threadStore = useActiveThreadStoreApi();
    const ownsActivationRef = useRef(false);

    useEffect(() => {
        if (is.null(store)) return;

        const owns = (activation: CommentsGroupActivation | null): boolean =>
            !is.null(activation) && (is.null(memberId) || activation.memberId === memberId);

        // Held while mirroring downwards so the resulting local change is not read back as a
        // local release, which would immediately undo the activation.
        let isMirroring = false;

        const mirror = (threadId: string | null): void => {
            isMirroring = true;
            threadStore.getState().setActiveThreadId(threadId);
            isMirroring = false;
        };

        const apply = (activation: CommentsGroupActivation | null): void => {
            if (owns(activation) && !is.null(activation)) {
                ownsActivationRef.current = true;
                mirror(activation.threadId);
                return;
            }
            if (!ownsActivationRef.current) return;
            ownsActivationRef.current = false;
            mirror(null);
        };

        apply(store.getState().activation);

        const unsubscribeGroup = store.subscribe((state, previous) => {
            if (state.activation === previous.activation) return;
            apply(state.activation);
        });

        const unsubscribeLocal = threadStore.subscribe((state, previous) => {
            if (isMirroring) return;
            if (state.activeThreadId === previous.activeThreadId) return;

            const activation = store.getState().activation;

            if (!owns(activation)) return;
            if (state.activeThreadId === activation?.threadId) return;

            // Cleared first, so the clearActivation notification finds nothing to mirror back.
            ownsActivationRef.current = false;
            store.getState().clearActivation();
        });

        return () => {
            unsubscribeGroup();
            unsubscribeLocal();
        };
    }, [memberId, store, threadStore]);
};

export const useGroupThreadActivationBridge = (memberId: string): void => {
    useGroupActivationMirror(memberId);
};

export const useGroupStreamActivationMirror = (): void => {
    useGroupActivationMirror(null);
};
